import { usePartFinderExport } from '@/hooks/api/part-finder-reports/usePartFinderExport';
import { pluralize } from '@/utils/format';
import BlockIcon from '@mui/icons-material/BlockOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import FileDownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import { Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import { BulkCancelActivityModal } from './BulkCancelActivityModal';
import { BulkDeleteActivityModal } from './BulkDeleteActivityModal';
import { BulkDeleteReportsModal } from './BulkDeleteReportsModal';

const Sidebar = styled(Box, {
  shouldForwardProp: property => property !== 'isOpen',
})<{ isOpen: boolean }>(({ theme, isOpen }) => ({
  display: isOpen ? 'flex' : 'none',
  width: 60,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(1),
  gap: theme.spacing(1),
  flexShrink: 0,
}));

const ActionButton = styled(IconButton)({
  width: 40,
  height: 40,
});

export const BulkActionSidebar = ({
  selectedReportIds,
  selectedBatchIds = [],
  cancellableReportIds = [],
  cancellableBatchIds = [],
  onDelete,
  onCancel,
}: {
  selectedReportIds: string[];
  selectedBatchIds?: string[];
  cancellableReportIds?: string[];
  cancellableBatchIds?: string[];
  onDelete: () => void;
  onCancel?: () => void;
}) => {
  const { exportToExcel, isExporting } = usePartFinderExport();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const handleExport = () => exportToExcel({ reportIds: selectedReportIds, batchIds: selectedBatchIds });

  const handleDeleteComplete = () => {
    onDelete();
    setDeleteModalOpen(false);
  };

  const handleCancelComplete = () => {
    onCancel?.();
    setCancelModalOpen(false);
  };

  const selectedCount = selectedReportIds.length + selectedBatchIds.length;
  const cancellableCount = cancellableReportIds.length + cancellableBatchIds.length;
  const shouldShowSidebar = selectedCount > 0;
  const hasBatchSelection = selectedBatchIds.length > 0;

  return (
    <>
      <Sidebar isOpen={shouldShowSidebar}>
        <Tooltip title={`Export ${pluralize(selectedCount, 'selected item', 'selected items')} to Excel`}>
          <span>
            <ActionButton color='primary' onClick={() => void handleExport()} disabled={isExporting}>
              {isExporting ? <CircularProgress size={20} /> : <FileDownloadIcon />}
            </ActionButton>
          </span>
        </Tooltip>
        {cancellableCount > 0 && (
          <Tooltip title={`Cancel ${pluralize(cancellableCount, 'selected item', 'selected items')}`}>
            <span>
              <ActionButton color='warning' onClick={() => setCancelModalOpen(true)}>
                <BlockIcon />
              </ActionButton>
            </span>
          </Tooltip>
        )}
        <Tooltip title={`Delete ${pluralize(selectedCount, 'selected item', 'selected items')}`}>
          <span>
            <ActionButton color='error' onClick={() => setDeleteModalOpen(true)}>
              <DeleteIcon />
            </ActionButton>
          </span>
        </Tooltip>
      </Sidebar>
      {hasBatchSelection ? (
        <BulkDeleteActivityModal
          open={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          selectedBatchIds={selectedBatchIds}
          selectedReportIds={selectedReportIds}
          onDeleteComplete={handleDeleteComplete}
        />
      ) : (
        <BulkDeleteReportsModal
          open={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          selectedReportIds={selectedReportIds}
          onDeleteComplete={handleDeleteComplete}
        />
      )}
      <BulkCancelActivityModal
        open={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        cancellableBatchIds={cancellableBatchIds}
        cancellableReportIds={cancellableReportIds}
        onCancelComplete={handleCancelComplete}
      />
    </>
  );
};
